import { createSlice } from '@reduxjs/toolkit';
import * as oPath from '../../../Lib/Object/Path/Mutable';
import { applySyncPatch, getDefaultDataSyncState, propertyRequiresSync } from '../DataSync';
import { SyncAction, SyncResolveAction } from '../DataSync/types';
import { INuclearGaugeStandardisationState, InitAction, InitResolveAction } from './types';

const name = 'worksheetEditor.loadIncrements';

const initialState: INuclearGaugeStandardisationState = {
  query: {},
  equipmentId: undefined,
  config: {},
  status: {
    // different operation progress
    synchronising: false,

    // calculated
    saving: false,
    ready: false,
    dirty: false,
  },
  ...getDefaultDataSyncState(),
};

const slice = createSlice({
  name,
  initialState,
  reducers: {
    init: (state, action: InitAction) => {
      const { payload: { equipmentId } = {} } = action;
      state.equipmentId = equipmentId;
      Object.entries(getDefaultDataSyncState()).forEach(([key, val]) => ((state as any)[key] = val));
      state.status.ready = false;
      state.status.synchronising = true;
    },
    initResolve: (state, action: InitResolveAction) => {
      const { payload: { standardisation, config } = {} } = action;
      state.config = config;
      Object.entries(getDefaultDataSyncState(standardisation)).forEach(([key, val]) => ((state as any)[key] = val));
      state.status.ready = true;
      state.status.synchronising = false;
    },
    initReject: (state) => {
      state.status.ready = true;
      state.status.synchronising = false;
    },

    sync: (state, action: SyncAction) => {
      state.status.synchronising = true;
    },
    syncResolve: (state, action: SyncResolveAction) => {
      state.status.synchronising = false;

      if (action?.payload?.syncPatch) {
        applySyncPatch(state, action.payload.syncPatch);
      }
      state.status.dirty = false;
    },
    setValue: (state, { payload: { name, value } = {} }) => {
      oPath.set(state, `syncModel.${name}`, value, true);
      state.status.dirty = propertyRequiresSync(state, name);
    },
    addArrayEntry: (state, { payload: { index, name, data = {} } = {} }) => {
      oPath.createPathIfNotExists(state, `syncModel.${name}`);
      let oldValue = oPath.get(state, `syncModel.${name}`);

      // We may have phantom records that are placeholders for records we want to exist. When adding a new record we
      // want those phantomt records to be created as well, otherwise it will create the first record only
      const gapsToFill = Math.max((index ?? 0) - (oldValue ?? []).length, 0);
      if (!oldValue) oPath.set(state, `syncModel.${name}`, [...Array(gapsToFill).fill({}), data], true);
      else oldValue.splice(index, 0, ...Array(gapsToFill).fill({}), data);
    },
    removeArrayEntry: (state, { payload: { index, name } = {} }) => {
      oPath.createPathIfNotExists(state, `syncModel.${name}`);
      let oldValue = oPath.get(state, `syncModel.${name}`);
      if (!oldValue) oPath.set(state, `syncModel.${name}`, [], true);
      else oldValue.splice(index, 1);
    },
    setQueryValue: (state, { payload: { name, value } = {} }) => {
      if (value === undefined) oPath.del(state, `query.${name}`);
      else oPath.set(state, `query.${name}`, value, true);
    },
  },
});

const { actions, reducer } = slice;

export { actions, reducer };
